import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Box, Inline } from '@atlaskit/primitives/compiled';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { isNonPublic } from '../../../../../../../common/utils/is-non-public';
import { resolveGitNounAuthors } from '../../../../../../../common/utils/resolve-git-noun-authors';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import type { searchGraphCommit_AtlassianSearchPage$key } from './__generated__/searchGraphCommit_AtlassianSearchPage.graphql';
import { messages } from './messages';

/**
 * Extracts a short commit hash from a commit URL.
 * Supports GitHub and Bitbucket URL formats where the full hash is the last path segment.
 * Returns the first 6 characters of the hash, or null if not found.
 */
const extractCommitHash = (url: string): string | null => {
	try {
		const { pathname } = new URL(url);
		const segments = pathname.split('/').filter(Boolean);
		const lastSegment = segments[segments.length - 1];
		if (lastSegment && /^[0-9a-f]{6,40}$/i.test(lastSegment)) {
			return lastSegment.slice(0, 6);
		}
		return null;
	} catch {
		return null;
	}
};

export const SearchGraphCommit = ({
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	selectedProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchGraphResultProps<searchGraphCommit_AtlassianSearchPage$key>): React.JSX.Element => {
	/* eslint-disable @atlassian/relay/unused-fields -- picture fields are consumed indirectly via resolveGitNounAuthors */
	const data = useFragment(
		graphql`
			fragment searchGraphCommit_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				title
				excerpt
				lastModifiedDate
				url
				permissionLevel
				initialContributors {
					name
					picture
				}
				entity {
					... on ExternalCommit {
						author {
							user {
								name
								picture
							}
						}
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalRepository {
								name
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);
	/* eslint-enable @atlassian/relay/unused-fields */

	const { formatMessage } = useIntl();

	const { lastModifiedDate, url, permissionLevel, title, excerpt, entity } = data;
	const { author, container } = entity || {};

	const resolvedAuthor = resolveGitNounAuthors(data.initialContributors, author?.user);
	const label = formatMessage(messages.resultLabel);

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} message={messages.committed} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const messageDescription = <DescriptionBody description={processExcerpt(excerpt, true)} />;

	const commitHash = extractCommitHash(url);

	const headerMetadataParts = [
		label,
		['repository-name', container?.name ?? null],
		['padlock', padlockIcon],
		['commit-hash', commitHash],
		['last-modified', lastModifiedComponent],
	];

	if (resolvedAuthor) {
		const [authorName, authorPicture] = resolvedAuthor;
		const authorContent = (
			<Inline space="space.025" alignBlock="start">
				<ProfileAvatar name={authorName} picture={authorPicture} />
				<Box>{authorName}</Box>
			</Inline>
		);
		headerMetadataParts.push(['author', authorContent]);
	}

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			productConfigs={productConfigs}
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<SearchResult
			productConfigs={productConfigs}
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={messageDescription}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			position={position}
			partial={data}
			appMode={appMode}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};
